import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { db } from '../firebase';
import { guardContentSubmission, showModerationWarning } from '../utils/moderation';
import { toPlainText } from '../utils/plainText';
import { createProfileDisclosure } from '../utils/profileDisclosure';
import { notificationService } from '../services/notificationService';
import { applicationService } from '../services/applicationService';
import {
  doc, getDoc, setDoc, collection, query, orderBy, limit, onSnapshot,
  addDoc, updateDoc, serverTimestamp, deleteDoc, where, getDocs
} from 'firebase/firestore';
import { showToast } from '../utils/toast';
import './Chat.css';

const REPORT_REASON_OPTIONS = [
  { value: 'violence_threat', label: '暴力・脅迫' },
  { value: 'romance_solicitation', label: 'ナンパ・恋愛目的の勧誘' },
  { value: 'dangerous_hiking_plan', label: '危険な登山計画・行動' },
  { value: 'last_minute_cancel_or_nuisance', label: 'ドタキャン・迷惑行為' },
  { value: 'impersonation_false_info', label: 'なりすまし・虚偽情報' },
  { value: 'personal_info_abuse', label: '個人情報悪用' },
  { value: 'other', label: 'その他' }
];

const REPORT_RECEIVED_MESSAGE = '通報を受け付けました。通報内容を確認してから、原則24時間以内に一次判断、最大48時間以内に確定判断で対応します。開発者X（https://x.com/yamaloglist）でもDM頂けますと状況確認が早くできますので、ご協力お願いいたします。';

function Chat() {
  const { chatId } = useParams();
  const { currentUser } = useAuth();
  const [chat, setChat] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [isBlocked, setIsBlocked] = useState(false);
  const [isBlockedBy, setIsBlockedBy] = useState(false);
  const [showReportUserForm, setShowReportUserForm] = useState(false);
  const [reportingMessageId, setReportingMessageId] = useState(null);
  const [reportReason, setReportReason] = useState('');
  const [reportComment, setReportComment] = useState('');
  const [recruitment, setRecruitment] = useState(null);
  const [targetApplication, setTargetApplication] = useState(null);
  const [isOrganizer, setIsOrganizer] = useState(false);
  const [otherUserNickname, setOtherUserNickname] = useState('');
  const [privateInfoShared, setPrivateInfoShared] = useState(false);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    if (!chatId || !currentUser) return;

    let unsubscribeMessages = null;

    const chatRef = doc(db, 'chats', chatId);
    getDoc(chatRef).then(async docSnap => {
      if (docSnap.exists()) {
        const data = docSnap.data();
        if (!data.participants.includes(currentUser.uid)) {
          showToast({ message: 'このチャットへのアクセス権限がありません。', type: 'error' });
          setLoading(false);
          return;
        }
        setChat({ id: docSnap.id, ...data });

        const otherUid = data.participants.find(uid => uid !== currentUser.uid);

        // 相手のFirestoreニックネームを取得
        if (otherUid) {
          try {
            const otherUserDoc = await getDoc(doc(db, 'users', otherUid));
            if (otherUserDoc.exists()) {
              const d = otherUserDoc.data();
              setOtherUserNickname(d.nickname || d.displayName || data.participantNames?.[otherUid] || '');
            }
          } catch (_) {}
        }

        // ブロック状態確認
        if (otherUid) {
          const blockId = `${currentUser.uid}_${otherUid}`;
          const blockByOtherId = `${otherUid}_${currentUser.uid}`;
          Promise.all([
            getDoc(doc(db, 'blocks', blockId)),
            getDoc(doc(db, 'blocks', blockByOtherId))
          ]).then(([myBlock, theirBlock]) => {
            setIsBlocked(myBlock.exists());
            setIsBlockedBy(theirBlock.exists());
          });
        }

        // 募集情報を取得
        if (data.recruitmentId) {
          try {
            const recDoc = await getDoc(doc(db, 'recruitments', data.recruitmentId));
            if (recDoc.exists()) {
              const recData = { id: recDoc.id, ...recDoc.data() };
              setRecruitment(recData);

              // 主催者かどうか確認
              if (recData.userId === currentUser.uid) {
                setIsOrganizer(true);
                // 相手の応募を取得
                if (otherUid) {
                  const appsQuery = query(
                    collection(db, 'applications'),
                    where('recruitmentId', '==', data.recruitmentId),
                    where('userId', '==', otherUid)
                  );
                  const appsSnap = await getDocs(appsQuery);
                  if (!appsSnap.empty) {
                    setTargetApplication({ id: appsSnap.docs[0].id, ...appsSnap.docs[0].data() });
                  }
                }
              }
            }
          } catch (e) {
            console.warn('募集情報の取得に失敗しました:', e);
          }
        }

        // 参加者確認後にメッセージ購読を開始（Rulesのget()競合を防ぐ）
        const messagesQuery = query(
          collection(db, 'chats', chatId, 'messages'),
          orderBy('createdAt', 'asc'),
          limit(100)
        );
        unsubscribeMessages = onSnapshot(messagesQuery, snapshot => {
          setMessages(snapshot.docs.map(d => ({ id: d.id, ...d.data() })));
        });
      }
      setLoading(false);
    });

    return () => unsubscribeMessages?.();
  }, [chatId, currentUser]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSend = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || sending) return;
    if (isBlocked || isBlockedBy) return;
    setSending(true);
    try {
      // Firestoreからニックネームを使用
      const myProfileDoc = await getDoc(doc(db, 'users', currentUser.uid));
      const myNickname = myProfileDoc.exists()
        ? (myProfileDoc.data().nickname || currentUser.displayName || '名無しの登山者')
        : (currentUser.displayName || '名無しの登山者');

      const moderation = await guardContentSubmission({
        action: 'chat_message',
        contentType: 'chat_message',
        texts: [newMessage],
        metadata: {
          chatId,
          recruitmentId: chat?.recruitmentId || '',
        },
      });
      showModerationWarning(moderation);

      await addDoc(collection(db, 'chats', chatId, 'messages'), {
        senderId: currentUser.uid,
        senderName: myNickname,
        text: newMessage.trim(),
        moderationFlags: moderation.flagged ? moderation.categories : [],
        flaggedTerms: moderation.flagged ? moderation.matchedTerms : [],
        createdAt: serverTimestamp()
      });
      await updateDoc(doc(db, 'chats', chatId), {
        lastMessage: newMessage.trim(),
        lastMessageAt: serverTimestamp()
      });
      setNewMessage('');
    } catch (err) {
      console.error('メッセージの送信に失敗しました:', err);
    } finally {
      setSending(false);
    }
  };

  const handleApproveApplication = async () => {
    if (!targetApplication) return;
    try {
      await applicationService.approve(targetApplication.id);
      setTargetApplication(prev => ({ ...prev, applicationStatus: 'approved' }));
      showToast({ message: '応募を承認しました。', type: 'success' });
    } catch (err) {
      console.error('承認に失敗しました:', err);
      showToast({ message: '承認に失敗しました。', type: 'error' });
    }
  };

  const handleRejectApplication = async () => {
    if (!targetApplication) return;
    if (!window.confirm('この応募を拒否しますか？')) return;
    try {
      await applicationService.reject(targetApplication.id);
      setTargetApplication(prev => ({ ...prev, applicationStatus: 'rejected' }));
      showToast({ message: '応募を拒否しました。', type: 'success' });
    } catch (err) {
      console.error('拒否に失敗しました:', err);
      showToast({ message: '拒否に失敗しました。', type: 'error' });
    }
  };

  const handleBlockToggle = async () => {
    if (!chat) return;
    const otherUid = chat.participants.find(uid => uid !== currentUser.uid);
    const blockId = `${currentUser.uid}_${otherUid}`;
    const blockRef = doc(db, 'blocks', blockId);

    if (isBlocked) {
      if (!window.confirm('ブロックを解除しますか？')) return;
      await deleteDoc(blockRef);
      setIsBlocked(false);
    } else {
      if (!window.confirm('このユーザーをブロックしますか？\nブロック中はメッセージを送受信できません。')) return;
      await setDoc(blockRef, {
        blockerId: currentUser.uid,
        blockedId: otherUid,
        createdAt: serverTimestamp()
      });
      setIsBlocked(true);
    }
  };

  const handleReportUser = async () => {
    if (!reportReason) { showToast({ message: '通報理由を選択してください。', type: 'error' }); return; }
    const otherUid = chat?.participants.find(uid => uid !== currentUser.uid);
    try {
      await addDoc(collection(db, 'reports'), {
        type: 'user',
        targetId: otherUid,
        targetName: chat?.participantNames?.[otherUid] || '不明',
        reporterId: currentUser.uid,
        reason: reportReason,
        comment: reportComment,
        chatId,
        decisionStatus: 'pending',
        reviewedBy: '',
        reviewedAt: null,
        actionTaken: 'none',
        createdAt: serverTimestamp()
      });
      await notificationService.create(currentUser.uid, 'report_received', {
        message: REPORT_RECEIVED_MESSAGE,
        targetType: 'user',
        targetId: otherUid,
        targetName: chat?.participantNames?.[otherUid] || '不明',
        chatId
      });
      showToast({ message: '通報を受け付けました。運営が確認します。', type: 'success' });
      setShowReportUserForm(false);
      setReportReason('');
      setReportComment('');
    } catch (err) {
      console.error('通報に失敗しました:', err);
      showToast({ message: '通報に失敗しました。', type: 'error' });
    }
  };

  const handleReportMessage = async () => {
    if (!reportReason) { showToast({ message: '通報理由を選択してください。', type: 'error' }); return; }
    const msg = messages.find(m => m.id === reportingMessageId);
    try {
      await addDoc(collection(db, 'reports'), {
        type: 'message',
        targetId: reportingMessageId,
        targetName: msg?.text?.slice(0, 50) || '',
        senderId: msg?.senderId || '',
        reporterId: currentUser.uid,
        reason: reportReason,
        comment: reportComment,
        chatId,
        decisionStatus: 'pending',
        reviewedBy: '',
        reviewedAt: null,
        actionTaken: 'none',
        createdAt: serverTimestamp()
      });
      await notificationService.create(currentUser.uid, 'report_received', {
        message: REPORT_RECEIVED_MESSAGE,
        targetType: 'message',
        targetId: reportingMessageId,
        targetName: msg?.text?.slice(0, 50) || '',
        chatId
      });
      showToast({ message: 'メッセージを通報しました。', type: 'success' });
      setReportingMessageId(null);
      setReportReason('');
      setReportComment('');
    } catch (err) {
      console.error('通報に失敗しました:', err);
      showToast({ message: '通報に失敗しました。', type: 'error' });
    }
  };

  const closeReport = () => {
    setShowReportUserForm(false);
    setReportingMessageId(null);
    setReportReason('');
    setReportComment('');
  };

  const handleSharePrivateInfo = async () => {
    if (!chat || !otherUserId) return;
    if (!window.confirm(`${otherUserName}さんに非公開情報を公開しますか？`)) return;
    try {
      await createProfileDisclosure({
        ownerId: currentUser.uid,
        viewerId: otherUserId,
        chatId,
        recruitmentId: chat.recruitmentId || '',
        recruitment,
        createdByAction: 'private_chat_share_button',
      });
      setPrivateInfoShared(true);
      showToast({ message: '非公開情報を公開しました。', type: 'success' });
    } catch (err) {
      console.error('非公開情報の公開に失敗しました:', err);
      showToast({ message: '非公開情報の公開に失敗しました。', type: 'error' });
    }
  };

  if (loading) return <div className="loading">読み込み中...</div>;
  if (!chat) return <div className="error">チャットが見つかりません。</div>;

  const otherUserId = chat.participants.find(uid => uid !== currentUser.uid);
  const otherUserName = otherUserNickname || chat.participantNames?.[otherUserId] || '相手';
  const recruitmentName = chat.recruitmentName || recruitment?.mountainName || recruitment?.groupName || '';

  const ReportForm = ({ onSubmit }) => (
    <div className="report-form-inline">
      <select
        className="form-control"
        value={reportReason}
        onChange={e => setReportReason(e.target.value)}
        style={{ fontSize: '0.9rem', marginBottom: '8px' }}
      >
        <option value="">理由を選択</option>
        {REPORT_REASON_OPTIONS.map(option => (
          <option key={option.value} value={option.value}>{option.label}</option>
        ))}
      </select>
      <textarea
        className="form-control"
        value={reportComment}
        onChange={e => setReportComment(e.target.value)}
        placeholder="詳細（任意）"
        rows={2}
        style={{ fontSize: '0.85rem', marginBottom: '8px' }}
      />
      <div style={{ display: 'flex', gap: '8px' }}>
        <button className="btn btn-danger" style={{ fontSize: '0.85rem', padding: '6px 14px' }} onClick={onSubmit}>通報する</button>
        <button className="btn btn-secondary" style={{ fontSize: '0.85rem', padding: '6px 14px' }} onClick={closeReport}>キャンセル</button>
      </div>
    </div>
  );

  return (
    <div className="chat-container">
      <div className="chat-header card">
        <Link to="/chats" className="back-link">← 戻る</Link>
        <div>
          <h2 className={isBlocked || isBlockedBy ? 'blocked-label' : ''}>
            {recruitmentName
              ? (isOrganizer
                  ? `「${recruitmentName}」への応募者: `
                  : `「${recruitmentName}」主催者: `)
              : ''}
            <Link
              to={`/users/${otherUserId}`}
              style={{ color: 'inherit', textDecoration: 'underline', cursor: 'pointer' }}
            >
              {otherUserName}
            </Link>
            {!recruitmentName && ' とのチャット'}
            {isBlocked && <span className="block-badge">ブロック中</span>}
          </h2>
          {recruitment && (
            <p style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginTop: '2px' }}>
              <Link to={`/recruitments/${recruitment.id}`} style={{ color: 'var(--alpine)' }}>
                募集詳細を見る →
              </Link>
            </p>
          )}
        </div>
        <div className="chat-header-actions">
          <button
            className={`btn btn-sm ${isBlocked ? 'btn-secondary' : 'btn-outline'}`}
            onClick={handleBlockToggle}
            title={isBlocked ? 'ブロック解除' : 'ブロック'}
          >
            {isBlocked ? 'ブロック解除' : 'ブロック'}
          </button>
          <button
            className="btn btn-sm btn-outline"
            onClick={handleSharePrivateInfo}
            disabled={privateInfoShared || isBlocked || isBlockedBy}
          >
            {privateInfoShared ? '非公開情報を公開済み' : 'このメンバーに非公開情報を公開する'}
          </button>
          <button
            className="btn btn-sm btn-link report-btn"
            onClick={() => setShowReportUserForm(v => !v)}
          >
            通報
          </button>
        </div>
      </div>

      {/* 主催者向け承認エリア */}
      {isOrganizer && targetApplication && (
        <div className="card" style={{ padding: '12px 16px', marginBottom: '8px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
            <span style={{ fontSize: '0.9rem' }}>
              応募状況:&nbsp;
              {targetApplication.applicationStatus === 'pending' && <strong style={{ color: 'var(--gold)' }}>承認待ち</strong>}
              {targetApplication.applicationStatus === 'approved' && <strong style={{ color: 'var(--alpine)' }}>承認済み</strong>}
              {targetApplication.applicationStatus === 'rejected' && <strong style={{ color: 'var(--ember)' }}>拒否済み</strong>}
            </span>
            {targetApplication.applicationStatus === 'pending' && (
              <>
                <button className="btn btn-primary btn-sm" onClick={handleApproveApplication}>承認する</button>
                <button className="btn btn-danger btn-sm" onClick={handleRejectApplication}>拒否する</button>
              </>
            )}
          </div>
        </div>
      )}

      {showReportUserForm && (
        <div className="card" style={{ padding: '16px' }}>
          <p style={{ fontWeight: 600, marginBottom: '8px' }}>ユーザーを通報: {otherUserName}</p>
          <ReportForm onSubmit={handleReportUser} />
        </div>
      )}

      {(isBlocked || isBlockedBy) && (
        <div className="block-notice">
          {isBlocked ? 'このユーザーをブロックしています。メッセージの送信はできません。' : 'このユーザーにブロックされています。'}
        </div>
      )}

      <div className="chat-messages card">
        {messages.length === 0 && (
          <p className="no-messages">まだメッセージがありません。最初のメッセージを送りましょう！</p>
        )}
        {messages.map(msg => (
          <div key={msg.id} className={`message ${msg.senderId === currentUser.uid ? 'mine' : 'theirs'}`}>
            <div className="message-bubble">
              <p className="message-text">
                {msg.hiddenByReports ? 'このメッセージは通報により非表示です。' : toPlainText(msg.text)}
              </p>
              <div className="message-footer">
                <p className="message-time">
                  {msg.createdAt?.toDate ? msg.createdAt.toDate().toLocaleString('ja-JP') : ''}
                </p>
                {msg.senderId !== currentUser.uid && (
                  <button
                    className="msg-report-btn"
                    onClick={() => setReportingMessageId(reportingMessageId === msg.id ? null : msg.id)}
                    title="このメッセージを通報"
                  >
                    通報
                  </button>
                )}
              </div>
              {reportingMessageId === msg.id && (
                <div style={{ marginTop: '8px' }}>
                  <ReportForm onSubmit={handleReportMessage} />
                </div>
              )}
            </div>
          </div>
        ))}
        <div ref={messagesEndRef} />
      </div>

      <form onSubmit={handleSend} className="chat-input card">
        <input
          type="text"
          className="form-control"
          value={newMessage}
          onChange={e => setNewMessage(e.target.value)}
          placeholder={isBlocked || isBlockedBy ? 'ブロック中のためメッセージを送れません' : 'メッセージを入力...'}
          maxLength={500}
          disabled={isBlocked || isBlockedBy}
        />
        <button
          type="submit"
          className="btn btn-primary"
          disabled={sending || !newMessage.trim() || isBlocked || isBlockedBy}
        >
          {sending ? '送信中...' : '送信'}
        </button>
      </form>
    </div>
  );
}

export default Chat;
